import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, Send, CheckCircle, Download, Eye, Users, PenTool, FileSignature, XCircle, Trash2, RotateCcw, Archive } from 'lucide-react';
import { statusColors, statusLabels } from './constants';

function ContratoCard({ contrato, onEnviarParaAssinatura, onAssinar, onDownloadPDF, onVerDetalhes, onInativar, onReativar, onExcluirPDF, onExcluir, onRestaurar, isExcluido }) {
  return (
    <Card data-testid={`contrato-${contrato.id}`} className={
      isExcluido ? 'border-slate-200 bg-slate-50/50 opacity-80' :
      contrato.status === 'inativo' ? 'border-red-200 bg-red-50/30' : ''
    }>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">{contrato.numero_contrato}</CardTitle>
            <CardDescription className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              {contrato.cliente_nome}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {contrato.assinatura && (
              <Badge className="bg-emerald-100 text-emerald-800">
                <CheckCircle className="w-3 h-3 mr-1" />
                Assinado
              </Badge>
            )}
            <Badge className={statusColors[contrato.status] || 'bg-gray-100'}>
              {statusLabels[contrato.status] || contrato.status}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pb-2">
        <div className="grid grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-slate-500">Tipo:</span>
            <p className="font-medium capitalize">{contrato.tipo}</p>
          </div>
          <div>
            <span className="text-slate-500">Valor Mensal:</span>
            <p className="font-semibold">R$ {contrato.valor_mensal?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
          </div>
          <div>
            <span className="text-slate-500">Vigência:</span>
            <p>{contrato.data_inicio?.split('T')[0]} a {contrato.data_fim?.split('T')[0]}</p>
          </div>
          <div>
            <span className="text-slate-500">Dias p/ Vencer:</span>
            <p className={contrato.dias_para_vencer < 60 ? 'text-red-600 font-semibold' : contrato.dias_para_vencer < 90 ? 'text-amber-600 font-semibold' : ''}>
              {contrato.dias_para_vencer} dias
            </p>
          </div>
        </div>
        {contrato.status === 'inativo' && contrato.motivo_inativacao && (
          <div className="mt-2 p-2 bg-red-100 rounded text-sm text-red-700">
            <strong>Motivo da inativação:</strong> {contrato.motivo_inativacao}
          </div>
        )}
        {isExcluido && contrato.data_exclusao && (
          <div className="mt-2 p-2 bg-slate-100 rounded text-sm text-slate-600">
            <strong>Excluído em:</strong> {new Date(contrato.data_exclusao).toLocaleDateString('pt-BR')}
            {contrato.status_anterior_exclusao && <span className="ml-2">| Status anterior: {statusLabels[contrato.status_anterior_exclusao] || contrato.status_anterior_exclusao}</span>}
          </div>
        )}
      </CardContent>
      <CardFooter className="flex flex-wrap gap-2 pt-2">
        {isExcluido ? (
          <>
            {onRestaurar && (
              <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => onRestaurar(contrato.id)} data-testid={`btn-restaurar-contrato-${contrato.id}`}>
                <RotateCcw className="w-4 h-4 mr-1" />
                Restaurar
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => onVerDetalhes(contrato)} data-testid={`btn-detalhes-contrato-${contrato.id}`}>
              <Eye className="w-4 h-4 mr-1" />
              Detalhes
            </Button>
          </>
        ) : (
          <>
            {contrato.status === 'rascunho' && (
              <Button size="sm" onClick={() => onEnviarParaAssinatura(contrato.id)} data-testid={`btn-enviar-assinatura-${contrato.id}`}>
                <Send className="w-4 h-4 mr-1" />
                Enviar p/ Assinatura
              </Button>
            )}
            {contrato.status === 'aguardando_assinatura' && !contrato.assinatura && (
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => onAssinar(contrato)} data-testid={`btn-assinar-${contrato.id}`}>
                <PenTool className="w-4 h-4 mr-1" />
                Assinar
              </Button>
            )}
            {(contrato.status === 'ativo' || contrato.status === 'suspenso') && onInativar && (
              <Button size="sm" variant="destructive" onClick={() => onInativar(contrato)} data-testid={`btn-inativar-contrato-${contrato.id}`}>
                <XCircle className="w-4 h-4 mr-1" />
                Inativar
              </Button>
            )}
            {contrato.status === 'inativo' && onReativar && (
              <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => onReativar(contrato.id)} data-testid={`btn-reativar-contrato-${contrato.id}`}>
                <RotateCcw className="w-4 h-4 mr-1" />
                Reativar
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => onDownloadPDF(contrato.id)} data-testid={`btn-pdf-contrato-${contrato.id}`}>
              <Download className="w-4 h-4 mr-1" />
              PDF
            </Button>
            {contrato.assinatura && onExcluirPDF && (
              <Button size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" onClick={() => onExcluirPDF(contrato)} data-testid={`btn-excluir-pdf-${contrato.id}`}>
                <Trash2 className="w-4 h-4 mr-1" />
                Excluir PDF
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => onVerDetalhes(contrato)} data-testid={`btn-detalhes-contrato-${contrato.id}`}>
              <Eye className="w-4 h-4 mr-1" />
              Detalhes
            </Button>
            {onExcluir && (
              <Button size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" onClick={() => onExcluir(contrato)} data-testid={`btn-excluir-contrato-${contrato.id}`}>
                <Trash2 className="w-4 h-4 mr-1" />
                Excluir
              </Button>
            )}
          </>
        )}
      </CardFooter>
    </Card>
  );
}

export default function ContratosTab({ 
  contratos, contratosExcluidos,
  onNovoContrato, onEnviarParaAssinatura, onAssinar, onDownloadPDF, onVerDetalhes,
  onInativar, onReativar, onExcluirPDF, onExcluir, onRestaurar
}) {
  const [showExcluidos, setShowExcluidos] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex gap-2">
          <Button variant={!showExcluidos ? 'default' : 'outline'} size="sm" onClick={() => setShowExcluidos(false)} data-testid="btn-tab-contratos-ativos">
            Contratos ({contratos.length})
          </Button>
          <Button variant={showExcluidos ? 'default' : 'outline'} size="sm" onClick={() => setShowExcluidos(true)} data-testid="btn-tab-contratos-excluidos" className={showExcluidos ? '' : 'text-slate-500'}>
            <Archive className="w-4 h-4 mr-1" />
            Excluídos ({contratosExcluidos?.length || 0})
          </Button>
        </div>
        <Button onClick={onNovoContrato} data-testid="btn-novo-contrato">
          <Plus className="w-4 h-4 mr-2" />
          Novo Contrato
        </Button>
      </div>
      
      <div className="grid gap-4">
        {showExcluidos ? (
          contratosExcluidos?.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-slate-500">
                <Archive className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>Nenhum contrato excluído</p>
              </CardContent>
            </Card>
          ) : (
            contratosExcluidos?.map(contrato => (
              <ContratoCard
                key={contrato.id}
                contrato={contrato}
                isExcluido={true}
                onVerDetalhes={onVerDetalhes}
                onRestaurar={onRestaurar}
                onDownloadPDF={onDownloadPDF}
              />
            ))
          )
        ) : (
          contratos.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-slate-500">
                <FileSignature className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>Nenhum contrato encontrado</p>
                <p className="text-sm mt-1">Clique em "Novo Contrato" para criar um</p>
              </CardContent>
            </Card>
          ) : (
            contratos.map(contrato => (
              <ContratoCard
                key={contrato.id}
                contrato={contrato}
                isExcluido={false}
                onEnviarParaAssinatura={onEnviarParaAssinatura}
                onAssinar={onAssinar}
                onDownloadPDF={onDownloadPDF}
                onVerDetalhes={onVerDetalhes}
                onInativar={onInativar}
                onReativar={onReativar}
                onExcluirPDF={onExcluirPDF}
                onExcluir={onExcluir}
              />
            ))
          )
        )}
      </div>
    </div>
  );
}
